import React, {memo, ReactNode} from 'react';

import {Box} from '@mui/system';
import {IMAGES} from 'src/constants/images';
import {COLORS} from 'src/styles/colors';

import {ButtonContainer} from '@/app/shared-components';
import useWindowSize from '@/hooks/WindowSize/WindowSize';

interface EditInfoLayoutProps {
  illustrationSrc: string;
  title: string;
  description: string;
  disclaimer?: string;
  buttons: ReactNode;
}

const descriptionSx = {
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 400,
  fontSize: 14,
  lineHeight: '20px',
  letterSpacing: '0.14px',
  color: COLORS.ON_SURFACE_VARIANT,
  textAlign: 'center',
};

const disclaimerSx = {
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 400,
  fontSize: 14,
  lineHeight: '20px',
  letterSpacing: '0.14px',
  color: COLORS.CONTENT_LIGHT_TERTIARY,
  textAlign: 'center',
};

const mobileTitleSx = {
  fontFamily: 'Poppins, sans-serif',
  fontSize: 24,
  fontWeight: 600,
  lineHeight: '32px',
  letterSpacing: '-0.36px',
  color: COLORS.ON_SURFACE,
  textAlign: 'center',
};

const desktopTitleSx = {
  fontFamily: 'Poppins, sans-serif',
  fontSize: 22,
  fontWeight: 600,
  lineHeight: '28px',
  letterSpacing: '-0.22px',
  color: COLORS.ON_SURFACE,
  textAlign: 'center',
};

const desktopOuterSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  minHeight: '100vh',
  bgcolor: 'white',
};

const desktopInnerSx = {
  width: '100%',
  maxWidth: 560,
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  pt: '98px',
  pb: 4,
};

const logoContainerSx = {
  display: 'flex',
  justifyContent: 'center',
  py: 1,
};

const desktopContentSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  px: '32px',
  py: '16px',
};

const desktopTextSx = {
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

const desktopImageContainerSx = {
  height: 340,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const desktopButtonsSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

const mobileOuterSx = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  bgcolor: 'white',
};

const mobileLogoContainerSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  px: 2,
  pt: '63px',
  pb: 2,
};

const mobileContentSx = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  px: 3,
  pb: '40px',
  gap: '16px',
};

const mobileImageContainerSx = {
  height: 240,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const mobileTextSx = {
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  width: '100%',
};

function EditInfoLayout({
  illustrationSrc,
  title,
  description,
  disclaimer,
  buttons,
}: EditInfoLayoutProps) {
  const {isMobile} = useWindowSize();

  if (!isMobile) {
    return (
      <Box sx={desktopOuterSx}>
        <Box sx={desktopInnerSx}>
          <Box sx={logoContainerSx}>
            <img
              src={IMAGES.LOGO_TEXT_BLACK.src}
              height={IMAGES.LOGO_TEXT_BLACK.height}
              width={IMAGES.LOGO_TEXT_BLACK.width}
              alt="modak"
            />
          </Box>

          <Box sx={desktopContentSx}>
            <Box sx={desktopTextSx}>
              <Box sx={desktopTitleSx}>{title}</Box>
              <Box sx={descriptionSx}>{description}</Box>
            </Box>

            <Box sx={desktopImageContainerSx}>
              <img
                src={illustrationSrc}
                alt=""
                style={{
                  height: 240,
                  width: 'auto',
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>

            <Box sx={desktopButtonsSx}>
              {disclaimer && <Box sx={disclaimerSx}>{disclaimer}</Box>}
              {buttons}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={mobileOuterSx}>
      <Box sx={mobileLogoContainerSx}>
        <img
          src={IMAGES.LOGO_TEXT_BLACK.src}
          height={IMAGES.LOGO_TEXT_BLACK.height}
          width={IMAGES.LOGO_TEXT_BLACK.width}
          alt="modak"
        />
      </Box>

      <Box sx={mobileContentSx}>
        <Box sx={mobileImageContainerSx}>
          <img
            src={illustrationSrc}
            alt=""
            style={{
              height: 240,
              width: 'auto',
              maxWidth: '100%',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </Box>

        <Box sx={mobileTextSx}>
          <Box sx={mobileTitleSx}>{title}</Box>
          <Box sx={descriptionSx}>{description}</Box>
        </Box>
      </Box>

      <ButtonContainer sticky>
        {disclaimer && <Box sx={disclaimerSx}>{disclaimer}</Box>}
        {buttons}
      </ButtonContainer>
    </Box>
  );
}

export default memo(EditInfoLayout);
